import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_CONSTANTS } from '@app/core/constants/api.constants';
import { BaseApiService } from '@app/core/services/api/base-api/base-api.service';
import { BehaviorSubject, catchError, Observable, ReplaySubject, Subject, tap, throwError } from 'rxjs';
import { IStatusMessage } from '../../interfaces/status-message.interface';
import { ILoginDto } from './dto/login.dto';
import { IRegisterDto } from './dto/register.dto';
import { Router } from '@angular/router';
import { ROUTE_CONSTANTS } from '@app/core/constants/route.constant';
import { IUserData } from '../../interfaces/user-data.interface';
import { AUTH_CONSTANTS } from '../../constants/auth.constants';
import { IResetDto } from './dto/reset.dto';
import { IResetConfirmDto } from './dto/reset-confirm.dto';
import { IRegisterConfirmDto } from './dto/register-confirm.dto';
import { OnboardingTourStep } from '@app/shared/interfaces/onboarding.interface';
import { IUserStats } from '../../interfaces/user-stats.interface';

/**
 * AuthApiService manages authentication flow in the frontend:
 * - Initializes user session
 * - Logs in, logs out, registers
 * - Refreshes access tokens using HttpOnly cookies
 * - Caches user session in localStorage
 */
@Injectable({ providedIn: 'root' })
export class AuthApiService extends BaseApiService {
    // Internal state

    /** Tracks current user session state */
    private userSubject$ = new BehaviorSubject<IUserData | null>(null);

    /** Emits once when initialization completes (successful or not) */
    private initFinishedSubject$ = new ReplaySubject<void>(1);

    /** Fires on a successful sign-in with email and password, before `user$` gets the user */
    private signedInSubject$ = new Subject<IUserData>();

    /** Prevents repeated init execution */
    private isInitialized = false;

    // Public observables

    /** Public stream of current user */
    readonly user$ = this.userSubject$.asObservable();

    /** Emits once after `init()` completes, used to block app bootstrap until auth state is resolved */
    readonly inited$ = this.initFinishedSubject$.asObservable();

    /** A coach just signed in (not a restored session after a reload) */
    readonly signedIn$ = this.signedInSubject$.asObservable();

    constructor(
        http: HttpClient,
        private readonly router: Router
    ) {
        super(http, API_CONSTANTS.ENDPOINTS.AUTH);
    }

    /** Current user snapshot, for code that needs the value once rather than a stream. */
    get currentUser(): IUserData | null {
        return this.userSubject$.value;
    }

    /**
     * Initializes user session:
     * - Checks cached user in localStorage
     * - If token is valid — reuses it
     * - Otherwise — calls /refresh
     */
    init(): void {
        if (this.isInitialized) return;
        this.loadUser();

        this.isInitialized = true;
    }

    /**
     * Loads user from cache or triggers refresh token flow
     */
    private loadUser(): void {
        const cachedRaw = localStorage.getItem(AUTH_CONSTANTS.USER_CACHE_KEY);
        if (cachedRaw) {
            try {
                const cachedUser = JSON.parse(cachedRaw) as IUserData;

                // Check if token is still valid (exp in seconds)
                if (Date.now() < cachedUser.exp * 1000) {
                    this.userSubject$.next(cachedUser);

                    // Finish init early
                    this.initFinishedSubject$.next();
                    return;
                }
            } catch {
                localStorage.removeItem(AUTH_CONSTANTS.USER_CACHE_KEY);
            }
        }

        // No valid session — fallback to refresh flow
        this.refresh().subscribe();
    }

    /**
     * Caches user session and emits to user$
     */
    setUser(user: IUserData): void {
        this.userSubject$.next(user);
        localStorage.setItem(AUTH_CONSTANTS.USER_CACHE_KEY, JSON.stringify(user));
    }

    /**
     * Clears cached session and resets user$
     */
    removeUser(): void {
        localStorage.removeItem(AUTH_CONSTANTS.USER_CACHE_KEY);
        this.userSubject$.next(null);
    }

    /**
     * Updates only the used credits inside cached user session.
     * If user is not present, operation is ignored.
     */
    updateUsedCredits(usedCredits: number): void {
        const currentUser = this.userSubject$.value;
        if (!currentUser) return;

        // Avoid unnecessary writes if value did not change
        if (currentUser.plan?.usedCredits === usedCredits) return;

        const updatedUser: IUserData = {
            ...currentUser,
            plan: {
                ...currentUser.plan,
                usedCredits,
            },
        };

        this.setUser(updatedUser);
    }

    /**
     * Updates only the number of used free trial sessions inside cached user session.
     * If user or trial is not present, operation is ignored.
     */
    updateFreeSessionsUsed(used: number): void {
        const currentUser = this.userSubject$.value;
        if (!currentUser?.freeTrial || currentUser.freeTrial.used === used) return;

        this.setUser({ ...currentUser, freeTrial: { ...currentUser.freeTrial, used } });
    }

    /** Stores the name the coach gave their co-coach (the sidekick) in the cached user session. */
    updateSidekickName(name: string): void {
        const currentUser = this.userSubject$.value;
        if (!currentUser || currentUser.sidekickName === name) return;

        this.setUser({ ...currentUser, sidekickName: name });
    }

    /** Keeps the cached user in step with the saved onboarding step of the co-coach. */
    updateOnboardingTourStep(step: OnboardingTourStep): void {
        const currentUser = this.userSubject$.value;
        if (!currentUser || currentUser.onboardingTourStep === step) return;

        this.setUser({ ...currentUser, onboardingTourStep: step });
    }

    /**
     * Updates only the stats inside cached user session.
     * If user is not present, operation is ignored.
     */
    updateStats(stats: IUserStats): void {
        const currentUser = this.userSubject$.value;
        if (!currentUser) return;

        const updatedUser: IUserData = {
            ...currentUser,
            stats,
        };

        this.setUser(updatedUser);
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // LOGIN
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Sends login request.
     * On success — caches user and sets session state.
     *
     * @param body Login credentials (email, password)
     */
    login(body: ILoginDto): Observable<IStatusMessage> {
        return this.post<IStatusMessage>('login', body).pipe(
            tap((res: IStatusMessage) => {
                if (res.user) {
                    this.signedInSubject$.next(res.user);
                    this.setUser(res.user);
                }
            })
        );
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // REGISTER
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Sends registration request for a new user
     *
     * @param body Registration data (email, password)
     */
    register(body: IRegisterDto): Observable<IStatusMessage> {
        return this.post<IStatusMessage>('register', body);
    }

    /**
     * Confirms a user's email address using a token.
     * - This finalizes the registration process by activating the account.
     *
     * @param body Object containing confirmation token.
     * @returns Observable with confirmation result.
     */
    registerConfirm(body: IRegisterConfirmDto): Observable<IStatusMessage> {
        return this.post<IStatusMessage>('register-confirm', body);
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // RESET PASSWORD
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Initiates a password reset flow by sending a reset email to the user.
     *
     * @param body Object containing user's email.
     * @returns Observable with result message.
     */
    resetPassword(body: IResetDto): Observable<IStatusMessage> {
        return this.post<IStatusMessage>('reset', body);
    }

    /**
     * Completes password reset using token and new password.
     *
     * @param body Object containing reset token and new password.
     * @returns Observable with result message.
     */
    submitPasswordReset(body: IResetConfirmDto): Observable<IStatusMessage> {
        return this.post<IStatusMessage>('reset-confirm', body);
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // REFRESH TOKENS
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Refreshes tokens using HttpOnly cookies (handled server-side)
     * - On success: updates user session
     * - On failure: resets session, redirects to login
     *
     *  @returns Observable with status message or EMPTY if refresh failed.
     */
    refresh(): Observable<IStatusMessage> {
        return this.post<IStatusMessage>('refresh', {}).pipe(
            tap((res: IStatusMessage) => {
                if (res.user) {
                    this.setUser(res.user);
                }
                this.initFinishedSubject$.next(); // Ensure init completes
            }),
            catchError((err) => {
                this.removeUser();

                // Ensure init completes
                this.initFinishedSubject$.next();
                return throwError(() => err);
            })
        );
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // LOGOUT
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Logs out user by revoking refresh token and clearing cookies
     * - On success: clears session and navigates to login
     *
     * @returns Observable with logout result.
     */
    logout(): Observable<IStatusMessage> {
        return this.post<IStatusMessage>('logout', {}).pipe(
            tap(() => {
                this.removeUser();
                this.router.navigate([ROUTE_CONSTANTS.AUTH.ROOT, ROUTE_CONSTANTS.AUTH.LOGIN]);
            })
        );
    }
}
